import $i18n from 'panda-i18n';
import React, { forwardRef, useCallback, useEffect, useState, } from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { CnCalendar } from '@/components/cn-calendar';
import { SelectDrawer as CnSelectDrawer } from '@/components/cn-select/select-drawer';
import { CnReadonly } from '@/components/cn-readonly';
import { CnDatePickerView } from './date-picker-view';
import isFunction from 'lodash/isFunction';
import { isValidDate, toDate } from '@/utils/date';
import { mergeProps } from '@/components/cn-picker/with-default-props';
import { useFormLayout } from '@/form/cn-form-layout';
import { formatValue, getFormatStrFromPrecision, getPrecisionFromFormat, getValueFromDayjs, } from './util';
import './date-picker.scss';
const CnDatePicker = (props, ref) => {
    const defaultProps = {
        placeholder: $i18n.get({
            id: 'SelectDate',
            dm: '选择日期',
            ns: 'CnDatePickerPro',
        }),
        precision: 'day',
    };
    const { wrapperAlign } = useFormLayout();
    const { prefix = 'cn-ui-m-', value: valueProp, defaultValue, size: sizeProp, align: alignProp, iconType, placeholder, format, outputFormat, hasClear, disabled, readOnly, onChange = () => { }, onClear = () => { }, onCancel = () => { }, className, zIndex, precision, mode = 'picker', formLabel, insideFilter, extraFooterRender, ...others } = mergeProps(defaultProps, {
        ...props,
        precision: props.precision || getPrecisionFromFormat(props.format, props.showTime),
    });
    const [value, setValue] = useState(toDate(valueProp || defaultValue));
    const [pickerValue, setPickerValue] = useState(value || new Date());
    const [visible, setVisible] = useState(false);
    const align = alignProp || wrapperAlign || 'left';
    const size = sizeProp || 'medium';
    const isControlled = 'value' in props;
    useEffect(() => {
        if ('value' in props) {
            const newValue = toDate(valueProp || defaultValue);
            if (!isValidDate(newValue)) {
                setValue(undefined);
                return;
            }
            else if (newValue === value) {
                return;
            }
            setValue(newValue);
            setPickerValue(newValue);
        }
    }, [valueProp]);
    const handleCancel = (reason) => {
        setPickerValue(value || new Date());
        if (isFunction(onCancel)) {
            onCancel(reason);
        }
    };
    const handleChange = (d) => {
        setPickerValue(d);
    };
    const handleClear = () => {
        if (!isControlled) {
            setPickerValue(new Date());
            setValue(undefined);
        }
        if (onClear) {
            onClear();
        }
        if (onChange) {
            onChange(undefined, '');
        }
    };
    const getFormated = useCallback((newValue) => {
        const contentFormat = newValue && format && dayjs(newValue).format(format);
        const isWeekDay = precision === 'week-day';
        const contentWithprecision = newValue &&
            dayjs(newValue).format(getFormatStrFromPrecision(precision || 'day'));
        const contentWeek = newValue &&
            `${newValue.getFullYear()}年-${dayjs(newValue).isoWeek()}周${isWeekDay ? `-周${dayjs(newValue).isoWeekday()}` : ''}`;
        return ((precision?.includes('week')
            ? contentWeek
            : contentFormat || contentWithprecision) || '');
    }, [value, format, precision]);
    const handleOk = useCallback(() => {
        const newValue = pickerValue;
        if (!isControlled) {
            setValue(newValue);
        }
        const processTime = !precision ||
            ['year', 'month', 'day', 'week', 'week-day'].includes(precision);
        const v = outputFormat
            ? formatValue(dayjs(newValue), outputFormat)
            : getValueFromDayjs(dayjs(newValue), processTime, props.endOfDay);
        onChange?.(v, getFormated(newValue));
    }, [pickerValue, getFormated, props.endOfDay, precision, props.showTime]);
    // showTime 时的兼容逻辑，不必写 timePanelProps format
    let timePanelProps = props.timePanelProps;
    if (props.showTime && props.format && typeof props.format === 'string') {
        const timeFormat = props.format.trim().split(/\s+/)[1];
        if (timeFormat) {
            timePanelProps = { ...timePanelProps } || {};
            timePanelProps.format = timeFormat;
        }
    }
    if (readOnly) {
        return React.createElement(CnReadonly, null, getFormated(value));
    }
    const content = mode === 'calendar' ? (React.createElement(CnCalendar, { ...others, timePanelProps: timePanelProps, selectionMode: "single", value: pickerValue, onChange: (val) => {
            setPickerValue(val);
        } })) : (React.createElement(CnDatePickerView, { ...others, prefix: prefix, precision: precision, visible: visible, value: pickerValue, onSelect: (v) => handleChange(v) }));
    return (React.createElement(CnSelectDrawer, { disableScroll: true, hasClear: hasClear, content: getFormated(value), align: align, size: size, iconType: iconType, placeholder: placeholder, disabled: disabled, readOnly: readOnly, insideForm: props.insideForm, className: classNames(className, 'cn-ui-m-date-picker-pro'), drawerClassName: "cn-ui-m-date-picker-pro-drawer", onVisibleChange: setVisible, zIndex: zIndex, onOk: handleOk, onCancel: handleCancel, onClear: handleClear, formLabel: formLabel, insideFilter: insideFilter, buttonPosition: "bottom", ref: ref },
        content,
        extraFooterRender ? (React.createElement("div", { className: "cn-ui-m-date-picker-extra-footer" }, typeof extraFooterRender === 'function'
            ? extraFooterRender()
            : extraFooterRender)) : null));
};
CnDatePicker.displayName = 'CnDatePickerPro';
export default forwardRef(CnDatePicker);
